export interface CourseDateRange {
  startDate: string // YYYY-MM-DD
  endDate: string
}

export interface CourseDateRangeRow extends CourseDateRange {
  id: string
}

const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}$/

export function toISODateString(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

export function todayISODate(): string {
  return toISODateString(new Date())
}

export function parseISODate(s: string): Date | null {
  if (!ISO_DATE_RE.test(s)) return null
  const [y, m, d] = s.split('-').map(Number)
  const date = new Date(y, m - 1, d)
  if (date.getFullYear() !== y || date.getMonth() !== m - 1 || date.getDate() !== d) return null
  return date
}

export function formatDateRangeLabel(startDate: string, endDate: string): string {
  const start = parseISODate(startDate)
  const end = parseISODate(endDate)
  if (!start || !end) return `${startDate} – ${endDate}`
  const opts: Intl.DateTimeFormatOptions = { day: 'numeric', month: 'short', year: 'numeric' }
  return `${start.toLocaleDateString('en-GB', opts)} – ${end.toLocaleDateString('en-GB', opts)}`
}

export function createEmptyDateRangeRow(): CourseDateRangeRow {
  return { id: crypto.randomUUID(), startDate: '', endDate: '' }
}

/** Earliest start first; incomplete batches (no start date) stay at the end. */
export function sortDateRangeRows(rows: CourseDateRangeRow[]): CourseDateRangeRow[] {
  return [...rows].sort((a, b) => {
    const aStart = a.startDate || ''
    const bStart = b.startDate || ''
    if (!aStart && !bStart) return 0
    if (!aStart) return 1
    if (!bStart) return -1
    if (aStart !== bStart) return aStart.localeCompare(bStart)
    const aEnd = a.endDate || ''
    const bEnd = b.endDate || ''
    if (!aEnd && !bEnd) return 0
    if (!aEnd) return 1
    if (!bEnd) return -1
    return aEnd.localeCompare(bEnd)
  })
}

/** Preserve list order (for UI while editing). */
export function completedRangesFromRows(rows: CourseDateRangeRow[]): CourseDateRange[] {
  return rows
    .filter((r) => r.startDate && r.endDate && r.endDate >= r.startDate)
    .map(({ startDate, endDate }) => ({ startDate, endDate }))
}

/** Sorted payload for save / API. */
export function normalizeDateRangeRows(rows: CourseDateRangeRow[]): CourseDateRange[] {
  return sortDateRangeRows(rows)
    .filter((r) => r.startDate && r.endDate)
    .map(({ startDate, endDate }) => ({ startDate, endDate }))
}

/** Booking / API period token: start|end ISO dates */
export function periodToken(range: CourseDateRange): string {
  return `${range.startDate}|${range.endDate}`
}

export function parsePeriodToken(token: string): CourseDateRange | null {
  if (token.includes('|')) {
    const [startDate, endDate] = token.split('|')
    if (startDate && endDate && ISO_DATE_RE.test(startDate) && ISO_DATE_RE.test(endDate)) {
      return { startDate, endDate }
    }
  }
  if (ISO_DATE_RE.test(token)) {
    return { startDate: token, endDate: token }
  }
  return null
}

export function validateDateRanges(
  ranges: CourseDateRange[],
  today = todayISODate(),
): string | null {
  if (!ranges.length) return 'Add at least one course date range (start and end date)'

  for (let i = 0; i < ranges.length; i++) {
    const { startDate, endDate } = ranges[i]
    const label = `Date range ${i + 1}`

    if (!startDate || !endDate) return `${label}: start and end date are required`

    const start = parseISODate(startDate)
    const end = parseISODate(endDate)
    if (!start || !end) return `${label}: use valid dates`

    if (startDate < today) {
      return `${label}: start date cannot be before today`
    }
    if (end < start) {
      return `${label}: end date must be on or after start date`
    }
  }
  return null
}

type LegacySchedule = {
  dateRanges?: CourseDateRange[]
  startDate?: string | Date
  endDate?: string | Date
  monthStartDays?: { month: number; year?: number; startDay: number }[]
  activeMonths?: number[]
  startDay?: number
}

export function parseDateRangesFromCourse(
  schedule?: LegacySchedule,
  courseDurationDays = 1,
): CourseDateRangeRow[] {
  if (schedule?.dateRanges?.length) {
    return schedule.dateRanges.map((r) => ({
        id: crypto.randomUUID(),
        startDate: typeof r.startDate === 'string'
          ? r.startDate.slice(0, 10)
          : toISODateString(new Date(r.startDate as Date)),
        endDate: typeof r.endDate === 'string'
          ? r.endDate.slice(0, 10)
          : toISODateString(new Date(r.endDate as Date)),
      }))
  }

  if (schedule?.startDate && schedule?.endDate) {
    const s =
      typeof schedule.startDate === 'string'
        ? schedule.startDate.slice(0, 10)
        : toISODateString(new Date(schedule.startDate))
    const e =
      typeof schedule.endDate === 'string'
        ? schedule.endDate.slice(0, 10)
        : toISODateString(new Date(schedule.endDate))
    return [{ id: crypto.randomUUID(), startDate: s, endDate: e }]
  }

  const entries = schedule?.monthStartDays ?? []
  if (entries.length) {
    const duration = Math.max(1, courseDurationDays)
    return entries.map((entry) => {
      const year = entry.year ?? new Date().getFullYear()
      const start = new Date(year, entry.month - 1, entry.startDay)
      const end = new Date(start)
      end.setDate(end.getDate() + duration - 1)
      return {
        id: crypto.randomUUID(),
        startDate: toISODateString(start),
        endDate: toISODateString(end),
      }
    })
  }

  const months = schedule?.activeMonths ?? []
  if (months.length && schedule?.startDay) {
    const duration = Math.max(1, courseDurationDays)
    const today = new Date()
    return months.map((month) => {
      let year = today.getFullYear()
      if (month < today.getMonth() + 1) year += 1
      const start = new Date(year, month - 1, schedule.startDay!)
      const end = new Date(start)
      end.setDate(end.getDate() + duration - 1)
      return {
        id: crypto.randomUUID(),
        startDate: toISODateString(start),
        endDate: toISODateString(end),
      }
    })
  }

  return [createEmptyDateRangeRow()]
}

export function formatDateRangesSummary(ranges: CourseDateRange[]): string {
  if (!ranges.length) return 'No date ranges'
  return ranges.map((r) => formatDateRangeLabel(r.startDate, r.endDate)).join('; ')
}

const LEGACY_MONTH_RE = /^(\d{4})-(\d{2})$/

/** Display label for booking.selectedMonth (legacy YYYY-MM or start|end period token). */
export function formatBookingSelectedPeriod(
  selectedMonth: string | undefined,
  style: 'long' | 'short' = 'long',
): string {
  if (!selectedMonth) return ''

  const period = parsePeriodToken(selectedMonth)
  if (period) {
    return formatDateRangeLabel(period.startDate, period.endDate)
  }

  const monthMatch = selectedMonth.match(LEGACY_MONTH_RE)
  const yearStr = monthMatch?.[1]
  const monthStr = monthMatch?.[2]
  if (yearStr && monthStr) {
    const year = parseInt(yearStr, 10)
    const month = parseInt(monthStr, 10) - 1
    return new Date(year, month, 1).toLocaleDateString('en-US', {
      month: style === 'short' ? 'short' : 'long',
      year: 'numeric',
    })
  }

  return selectedMonth
}

/** Ranges still open for booking (end date >= today). */
export function getBookableDateRanges(
  ranges: CourseDateRange[] | undefined,
  today = todayISODate(),
): CourseDateRange[] {
  if (!ranges?.length) return []
  return ranges.filter((r) => r.endDate >= today)
}

export function hasScheduleContent(schedule?: {
  dateRanges?: CourseDateRange[]
  startDate?: unknown
  endDate?: unknown
  timeSlots?: unknown[]
  monthStartDays?: unknown[]
}): boolean {
  if (!schedule) return false
  return !!(
    (schedule.dateRanges && schedule.dateRanges.length > 0) ||
    schedule.startDate ||
    schedule.endDate ||
    (schedule.monthStartDays && schedule.monthStartDays.length > 0) ||
    (schedule.timeSlots && (schedule.timeSlots as unknown[]).length > 0)
  )
}
